type LogLevel = 'debug' | 'info' | 'warn' | 'error'

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3
}

type LoggerConfig = {
  defaultLevel?: LogLevel
}

const LOGGER_CONFIG: Record<string, LoggerConfig> = {
  'blob-sphere': { defaultLevel: 'warn' },
  'blob-sphere-debug': { defaultLevel: 'warn' },
  'blob-sdf': { defaultLevel: 'warn' },
  'webgpu-queue': { defaultLevel: 'warn' },
  'capture': { defaultLevel: 'warn' },
  'capture-view': { defaultLevel: 'warn' },
  'thumbnails': { defaultLevel: 'warn' },
  'thumbnail-ui': { defaultLevel: 'warn' },
  'document-loader': { defaultLevel: 'warn' },
  'persistence': { defaultLevel: 'warn' },
  'autosave': { defaultLevel: 'warn' }
}

export type LoggerNamespace = keyof typeof LOGGER_CONFIG

const overrides = new Map<string, LogLevel>()

function getLevel(namespace: LoggerNamespace): LogLevel {
  if (overrides.has(namespace)) {
    return overrides.get(namespace) ?? 'warn'
  }
  const config = LOGGER_CONFIG[namespace]
  return config?.defaultLevel ?? 'warn'
}

function shouldLog(namespace: LoggerNamespace, level: LogLevel): boolean {
  const currentLevel = getLevel(namespace)
  return LOG_LEVELS[level] >= LOG_LEVELS[currentLevel]
}

export function setLoggerLevel(namespace: LoggerNamespace, level: LogLevel): void {
  overrides.set(namespace, level)
}

export function getLoggerLevel(namespace: LoggerNamespace): LogLevel {
  return getLevel(namespace)
}

export type ScopedLogger = {
  namespace: LoggerNamespace
  level: () => LogLevel
  debugEnabled: () => boolean
  infoEnabled: () => boolean
  warnEnabled: () => boolean
  errorEnabled: () => boolean
  debug: (...args: unknown[]) => void
  info: (...args: unknown[]) => void
  warn: (...args: unknown[]) => void
  error: (...args: unknown[]) => void
}

function format(namespace: LoggerNamespace, args: unknown[]): unknown[] {
  if (args.length === 0) return [`[${namespace}]`]
  const [first, ...rest] = args
  if (typeof first === 'string') {
    return [`[${namespace}] ${first}`, ...rest]
  }
  return [`[${namespace}]`, first, ...rest]
}

export function createLogger(namespace: LoggerNamespace): ScopedLogger {
  return {
    namespace,
    level: () => getLevel(namespace),
    debugEnabled: () => shouldLog(namespace, 'debug'),
    infoEnabled: () => shouldLog(namespace, 'info'),
    warnEnabled: () => shouldLog(namespace, 'warn'),
    errorEnabled: () => shouldLog(namespace, 'error'),
    debug: (...args: unknown[]) => {
      if (!shouldLog(namespace, 'debug')) return
      console.debug(...format(namespace, args))
    },
    info: (...args: unknown[]) => {
      if (!shouldLog(namespace, 'info')) return
      console.info(...format(namespace, args))
    },
    warn: (...args: unknown[]) => {
      if (!shouldLog(namespace, 'warn')) return
      console.warn(...format(namespace, args))
    },
    error: (...args: unknown[]) => {
      if (!shouldLog(namespace, 'error')) return
      console.error(...format(namespace, args))
    },
  }
}
